<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue'
import { AudioStore } from '@/front/stores/audio_store.js'
import { preloadManager } from '@/front/services/PreloadManager.js'

const audioStore = AudioStore()
const audioElement = ref(null)
const isInitialized = ref(false)
const currentState = ref({
  currentTime: 0,
  duration: 0,
  ended: false,
  error: null,
  height: 0,
  muted: false,
  paused: true,
  playing: false,
  playbackRate: 1,
  seeking: false,
  started: false,
  volume: 1,
  waiting: false,
  // Vidstack compatibility properties
  currentSrc: '',
  controlsHidden: true,
  fullscreen: false,
  qualities: [],
  quality: null,
  seekableEnd: 0,
  seekableStart: 0,
  textTrack: null,
  controlsVisible: false,
  width: 0
})

const emit = defineEmits(['play', 'pause', 'seek', 'ended', 'timeupdate', 'error'])

const props = defineProps({
  controls: {
    type: Boolean,
    default: true,
    required: false
  },
  volume: {
    type: Number,
    default: 1,
    required: false
  }
})

// Create a mock player object that matches Vidstack interface
const mockPlayer = {
  play: () => {
    if (audioElement.value) {
      const playPromise = audioElement.value.play()
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          log.info(1, "FastAudio play error:", error)
          handleError(error)
        })
      }
    }
  },
  pause: () => {
    if (audioElement.value) {
      audioElement.value.pause()
    }
  },
  seek: (time) => {
    if (audioElement.value) {
      audioElement.value.currentTime = time
    }
  },
  subscribe: (callback) => {
    // Store the callback for state updates
    if (callback) {
      watch(currentState, (newState) => {
        callback(newState)
      }, { deep: true })
    }
  },
  state: currentState
}

// Update audio element properties based on props/store
const updateAudioProperties = () => {
  log.info(2, "FastAudio updateAudioProperties")
  if (!audioElement.value) {
    log.info(2, "FastAudio updateAudioProperties no audioElement")
    return
  }


  // Set volume from props
  audioElement.value.volume = props.volume || 1

  // Set source if available
  if (audioStore.src) {
    // Always check for preloaded clip first using the stored clipId
    const clipId = audioStore.clipId
    log.info(2, "FastAudio updateAudioProperties using clipId", clipId)

    if (clipId) {
      const preloaded = preloadManager.getPreloadedClip(clipId)
      log.info(2, "FastAudio updateAudioProperties checking for preloaded", clipId, preloaded)

      if (preloaded && preloaded.element && preloaded.element.readyState >= 2) {
        log.info(2, "🎯 FastAudio swapping to preloaded element", clipId)

        // Take ownership of the preloaded element
        const preloadedEl = preloadManager.takePreloadedElement(clipId)
        if (preloadedEl) {
          log.info(2, "FastAudio taking ownership of preloaded element", clipId)

          // Replace our element with the preloaded one
          const parent = audioElement.value.parentNode

          // Copy our event listeners to the preloaded element
          setupEventListeners(preloadedEl)

          // Remove old element and use preloaded one
          if (parent) {
            parent.replaceChild(preloadedEl, audioElement.value)
            audioElement.value = preloadedEl
          }

          // Set volume and other properties
          audioElement.value.volume = props.volume || 1
          audioElement.value.controls = props.controls
          audioElement.value.className = "w-96"

          log.info(2, "FastAudio element swapped, ready to play instantly")
        }

      } else if (audioElement.value.src !== audioStore.src) {
        log.info(2, "🔄 FastAudio loading audio fresh", clipId)
        audioElement.value.src = audioStore.src
        audioElement.value.load() // Force reload
      }
    }
  } else {
    log.info(2, "FastAudio updateAudioProperties no src")
  }
}

// Handle audio events and emit compatible events
const handleLoadedMetadata = () => {
  updateCurrentState()
  log.info(3, "FastAudio loadedmetadata")
}

const handleCanPlay = () => {
  updateCurrentState()
  log.info(3, "FastAudio canplay")

  // Auto-play immediately (matching AudioCore behavior)
  mockPlayer.play()

  // Emit Vidstack-compatible event
  emit('can-play', {
    trigger: 'can-play',
    detail: currentState.value
  })
}

const handlePlay = () => {
  updateCurrentState()
  log.info(3, "FastAudio play")

  // Mark performance timing - audio actually started playing
  audioStore.startedAt = Date.now()
  log.info(1, "audioStore.requestedAt", audioStore.requestedAt)
  log.info(1, "audioStore.startedAt", audioStore.startedAt)
  log.info(1, "FastAudio delay between requested and started", audioStore.startedAt - audioStore.requestedAt)

  // Performance tracking - just log the ID for now
  const srcUrl = audioStore.src
  if (srcUrl) {
    const idMatch = srcUrl.match(/([a-f0-9-]{36})/i)
    if (idMatch) {
      log.info(2, "FastAudio playback started for clip:", idMatch[1])
    }
  }

  // Set volume from props (AudioCore does this in onPlay)
  if (audioElement.value && props.volume !== undefined) {
    audioElement.value.volume = props.volume
  }

  emit('play', {
    trigger: 'play',
    detail: currentState.value
  })
}

const handlePause = () => {
  updateCurrentState()
  log.info(3, "FastAudio pause")
  emit('pause', {
    trigger: 'pause',
    detail: currentState.value
  })
}

const handleTimeUpdate = () => {
  updateCurrentState()
  audioStore.currentTime = currentState.value.currentTime

  emit('timeupdate', {
    trigger: 'time-update',
    detail: {
      currentTime: currentState.value.currentTime,
      duration: currentState.value.duration
    }
  })
}

const handleSeeking = () => {
  updateCurrentState()
  emit('seek', {
    trigger: 'seeking',
    detail: currentState.value
  })
}

const handleEnded = () => {
  updateCurrentState()
  log.info(3, "FastAudio ended")
  emit('ended', {
    trigger: 'ended',
    detail: currentState.value
  })
}

const handleError = (error) => {
  log.info(1, "FastAudio error:", error)
  currentState.value.error = error
  emit('error', {
    trigger: 'error',
    detail: { error }
  })
}

const handleWaiting = () => {
  currentState.value.waiting = true
}

const handleCanPlayThrough = () => {
  currentState.value.waiting = false
}

// Set up event listeners on an audio element
const setupEventListeners = (element) => {
  // Remove any existing listeners first
  element.removeEventListener('loadedmetadata', handleLoadedMetadata)
  element.removeEventListener('canplay', handleCanPlay)
  element.removeEventListener('play', handlePlay)
  element.removeEventListener('pause', handlePause)
  element.removeEventListener('timeupdate', handleTimeUpdate)
  element.removeEventListener('seeking', handleSeeking)
  element.removeEventListener('ended', handleEnded)
  element.removeEventListener('error', handleError)
  element.removeEventListener('waiting', handleWaiting)
  element.removeEventListener('canplaythrough', handleCanPlayThrough)

  // Add event listeners
  element.addEventListener('loadedmetadata', handleLoadedMetadata)
  element.addEventListener('canplay', handleCanPlay)
  element.addEventListener('play', handlePlay)
  element.addEventListener('pause', handlePause)
  element.addEventListener('timeupdate', handleTimeUpdate)
  element.addEventListener('seeking', handleSeeking)
  element.addEventListener('ended', handleEnded)
  element.addEventListener('error', handleError)
  element.addEventListener('waiting', handleWaiting)
  element.addEventListener('canplaythrough', handleCanPlayThrough)
}

// Update the current state object
const updateCurrentState = () => {
  if (!audioElement.value) return

  const audio = audioElement.value

  currentState.value = {
    ...currentState.value,
    currentTime: audio.currentTime || 0,
    duration: audio.duration || 0,
    ended: audio.ended,
    muted: audio.muted,
    paused: audio.paused,
    playing: !audio.paused && !audio.ended,
    playbackRate: audio.playbackRate,
    seeking: audio.seeking,
    started: audio.currentTime > 0 || !audio.paused,
    volume: audio.volume,
    currentSrc: audio.src,
    seekableEnd: audio.seekable.length > 0 ? audio.seekable.end(0) : 0,
    seekableStart: audio.seekable.length > 0 ? audio.seekable.start(0) : 0
  }

  // Update audioStore playerState to match Vidstack behavior
  audioStore.setPlayerState(currentState.value)
}

onMounted(() => {
  if (!audioElement.value) return

  // Set up event listeners using helper function
  setupEventListeners(audioElement.value)

  // Configure audio element
  audioElement.value.preload = 'auto'
  audioElement.value.volume = props.volume || 1

  // Set up the mock player interface for audioStore
  audioStore.player = mockPlayer
  isInitialized.value = true

  // Initialize with current source if available
  updateAudioProperties()

  log.info(3, "FastAudio mounted")
})

onBeforeUnmount(() => {
  if (audioElement.value) {
    // Clean up event listeners
    audioElement.value.removeEventListener('loadedmetadata', handleLoadedMetadata)
    audioElement.value.removeEventListener('canplay', handleCanPlay)
    audioElement.value.removeEventListener('play', handlePlay)
    audioElement.value.removeEventListener('pause', handlePause)
    audioElement.value.removeEventListener('timeupdate', handleTimeUpdate)
    audioElement.value.removeEventListener('seeking', handleSeeking)
    audioElement.value.removeEventListener('ended', handleEnded)
    audioElement.value.removeEventListener('error', handleError)
    audioElement.value.removeEventListener('waiting', handleWaiting)
    audioElement.value.removeEventListener('canplaythrough', handleCanPlayThrough)
  }
})

// Watch for source changes
watch(() => audioStore.src, (newSrc) => {
  if (newSrc && isInitialized.value) {
    log.info(3, "FastAudio source changed", newSrc)
    updateAudioProperties()
  }
})

// Watch for volume changes
watch(() => props.volume, (newVolume) => {
  if (audioElement.value && newVolume !== undefined) {
    audioElement.value.volume = newVolume
  }
})

// Computed property for dynamic source object (matching AudioCore format)
const audioSrc = computed(() => {
  return audioStore.src ? {
    src: audioStore.src,
    type: 'audio/mp3'
  } : null
})
</script>

<template>
  <div class="fast-audio-container">
    <!-- Native HTML5 audio element -->
    <audio
      ref="audioElement"
      :src="audioStore.src"
      :controls="props.controls"
      :volume="props.volume"
      preload="auto"
      class="w-96"
      style="width: 100%;"
    >
      <!-- Fallback for browsers that don't support audio -->
      Your browser does not support the audio element.
    </audio>

    <!-- Hidden track element for Vidstack compatibility -->
    <template v-if="audioStore.src">
      <track :src="audioStore.src" kind="audio" style="display: none;" />
    </template>
  </div>
</template>

<style scoped>
.fast-audio-container {
  width: 100%;
}

.fast-audio-container audio {
  width: 100%;
  max-width: 384px; /* w-96 equivalent */
}
</style>